import { useState } from "react";
import { useCreate, useUpdate, useGetIdentity } from "ra-core";
import { toast } from "sonner";
import { Check, Mail, Phone, RotateCw, Sparkles, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import type { Contact, NextActionType } from "../types";

// Sprint 10.x — Spec Théo : "relancer / appeler / envoyer démo / engager".
const NA_OPTIONS: {
  value: NextActionType;
  label: string;
  Icon: typeof Phone;
}[] = [
  { value: "a_relancer", label: "Relance", Icon: RotateCw },
  { value: "a_appeler", label: "Appel", Icon: Phone },
  { value: "a_mailer", label: "Envoyer démo", Icon: Mail },
  { value: "a_engager", label: "Engagement", Icon: Sparkles },
  { value: "aucune", label: "Aucune", Icon: X },
];

const NA_LABEL: Record<NextActionType, string> = {
  a_relancer: "à relancer",
  a_appeler: "à appeler",
  a_mailer: "à mailer pour démo",
  a_engager: "à engager",
  aucune: "sans suite",
};

function ymd(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function quickDate(offsetDays: number) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return ymd(d);
}

function formatDateFr(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

type Props = {
  contact: Contact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Dialog déclenché par "Marquer fait" du header. Crée une note timeline si
 * note remplie + met à jour next_action_type/date du contact. Stratégie
 * d'atomicité C (Sprint 2 Q3) : 2 mutations séquentielles, toast actionable
 * avec bouton "Réessayer" si l'update échoue après une note réussie.
 *
 * Cf design/atomic-crm/dialog.jsx (MarkDoneDialog).
 */
export function MarkDoneDialog({ contact, open, onOpenChange }: Props) {
  const { identity } = useGetIdentity();
  const [note, setNote] = useState("");
  const [naType, setNaType] = useState<NextActionType>("a_appeler");
  const [naDate, setNaDate] = useState<string>(quickDate(1));
  const [submitting, setSubmitting] = useState(false);

  const [createNote] = useCreate();
  const [updateContact] = useUpdate();

  const currentAction = contact.next_action_type;
  const currentDate = contact.next_action_date;
  const currentLabel = currentAction
    ? `${NA_LABEL[currentAction]}${currentDate ? ` le ${formatDateFr(currentDate)}` : ""}`
    : "ton action";

  const reset = () => {
    setNote("");
    setNaType("a_appeler");
    setNaDate(quickDate(1));
  };

  const tryUpdateContact = (
    nextType: NextActionType,
    nextDate: string | null,
  ) =>
    new Promise<void>((resolve, reject) => {
      updateContact(
        "contacts",
        {
          id: contact.id,
          data: {
            next_action_type: nextType,
            next_action_date: nextType === "aucune" ? null : nextDate,
          },
          previousData: contact,
        },
        {
          onSuccess: () => resolve(),
          onError: (err) => reject(err),
        },
      );
    });

  const handleSubmit = async () => {
    setSubmitting(true);
    const trimmed = note.trim();
    const effectiveDate = naType === "aucune" ? null : naDate;
    let noteCreated = false;

    // Étape 1 : créer la note timeline si textarea remplie.
    if (trimmed) {
      try {
        await new Promise<void>((resolve, reject) =>
          createNote(
            "contact_notes",
            {
              data: {
                contact_id: contact.id,
                text: trimmed,
                sales_id: identity?.id,
                date: new Date().toISOString(),
              },
            },
            {
              onSuccess: () => {
                noteCreated = true;
                resolve();
              },
              onError: (err) => reject(err),
            },
          ),
        );
      } catch {
        toast.error("Impossible d'ajouter la note", {
          description: "Ta note n'a pas été enregistrée. Réessaie.",
        });
        setSubmitting(false);
        return;
      }
    }

    // Étape 2 : update contact next_action_*. Si fail et la note a été
    // créée → toast actionable (option C, Sprint 2 Q3).
    try {
      await tryUpdateContact(naType, effectiveDate);
      toast.success("Action marquée comme faite");
      reset();
      onOpenChange(false);
    } catch {
      if (noteCreated) {
        toast.error(
          "Note ajoutée, mais l'action n'a pas pu être marquée comme faite",
          {
            description:
              "La note est dans la timeline. La prochaine action n'a pas été mise à jour.",
            action: {
              label: "Réessayer",
              onClick: () => {
                tryUpdateContact(naType, effectiveDate)
                  .then(() => {
                    toast.success("Action mise à jour");
                    reset();
                    onOpenChange(false);
                  })
                  .catch(() => {
                    toast.error("Échec à nouveau", {
                      description: "Vérifie ta connexion ou retente plus tard.",
                    });
                  });
              },
            },
            duration: 10000,
          },
        );
      } else {
        toast.error("Impossible de mettre à jour la prochaine action", {
          description: "Réessaie.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Marquer cette action comme faite</DialogTitle>
          <DialogDescription>
            Tu as effectué : <strong>{currentLabel}</strong>. Note ce que tu
            viens de faire et planifie la suite.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="md-note" className="text-[12px]">
              Note de fin (optionnel)
            </Label>
            <Textarea
              id="md-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex : appel de 15min, on a balayé les freins prix, il revient mercredi…"
              rows={4}
              className="text-[13.5px]"
            />
            <p className="text-[11.5px] text-muted-foreground">
              Sera enregistré comme une note dans la timeline.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-[12px]">Prochaine action</Label>
            <div className="grid grid-cols-4 gap-1.5">
              {NA_OPTIONS.map((opt) => {
                const I = opt.Icon;
                return (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={naType === opt.value ? "default" : "outline"}
                    size="sm"
                    className="h-8 text-[12.5px] justify-center gap-1.5"
                    onClick={() => setNaType(opt.value)}
                  >
                    <I className="size-3.5" />
                    {opt.label}
                  </Button>
                );
              })}
            </div>
            {naType !== "aucune" && (
              <>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { label: "Aujourd'hui", v: quickDate(0) },
                    { label: "Demain", v: quickDate(1) },
                    { label: "Dans 3 jours", v: quickDate(3) },
                    { label: "Dans 1 semaine", v: quickDate(7) },
                  ].map((opt) => (
                    <Button
                      key={opt.label}
                      type="button"
                      variant={naDate === opt.v ? "default" : "outline"}
                      size="sm"
                      className={cn("h-8 text-[12px]")}
                      onClick={() => setNaDate(opt.v)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                <input
                  type="date"
                  value={naDate}
                  onChange={(e) => setNaDate(e.target.value)}
                  className="w-full h-8 px-2 text-[12.5px] rounded-md border border-input bg-background"
                />
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="gap-1.5"
          >
            <Check className="size-3.5" />
            Enregistrer & passer au suivant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
